
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Card,
    Combobox,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    Link,
    makeStyles,
    Option,
    Select,
    Spinner,
    Switch,
    Textarea,
    tokens
} from '@fluentui/react-components';
import { Add24Regular, Delete24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { AnsprechpartnerItem } from '../../models/AnsprechpartnerItem';
import type { ProjektItem } from '../../models/ProjektItem';
import type { ProjektPartnerItem } from '../../models/ProjektPartnerItem';
import type { ProjektPartnerZuweisungItem } from '../../models/ProjektPartnerZuweisungItem';
import { DataService } from '../../services/dataService';
import ProjektpartnerDrawer from './ProjektpartnerDrawer';

// Role mappings
const PARTNER_ROLES = {
    1: 'Client',
    2: 'Lead Partner',
    3: 'Project Partner',
    4: 'Third Party Services',
} as const;

type PartnerRole = keyof typeof PARTNER_ROLES;

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    rolesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: tokens.spacingVerticalL,
    },
    roleSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    roleTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 'bold',
        color: tokens.colorNeutralForeground1,
        marginBottom: tokens.spacingVerticalS,
    },
    partnerCard: {
        padding: tokens.spacingHorizontalM,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ':hover': {
            boxShadow: tokens.shadow8,
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    addPartnerCard: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '150px',
        border: `2px dashed ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    addButtonContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: tokens.spacingVerticalS,
        color: tokens.colorBrandBackground,
    },
    partnerName: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 'bold',
        color: tokens.colorNeutralForeground1,
        marginBottom: tokens.spacingVerticalS,
    },
    partnerDetail: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
        marginBottom: tokens.spacingVerticalXS,
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: tokens.spacingVerticalM,
    },
    statusBadge: {
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
        borderRadius: tokens.borderRadiusSmall,
        fontSize: tokens.fontSizeBase200,
        fontWeight: 600,
        whiteSpace: 'nowrap' as const,
    },
    statusActive: {
        backgroundColor: '#90EE90',
        color: '#2D5F2E',
    },
    statusInactive: {
        backgroundColor: '#D3D3D3',
        color: '#666666',
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        padding: tokens.spacingHorizontalL,
    },
    detailsGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    detailLabel: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
    },
    detailValue: {
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorNeutralForeground1,
    },
    buttonGroup: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        marginTop: tokens.spacingVerticalL,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    formLabel: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
    },
});

interface EckdatenProjektpartnerProps {
    project: ProjektItem | null;
}

interface PartnerWithRole extends Omit<ProjektPartnerItem, 'itemId'> {
    rolle?: PartnerRole;
    rolleAsText?: string;
    zuweisungStatus?: string;
    isAktiv?: boolean;
    aktivVon?: string;
    finanzleistungenAsText?: string;
    personalleistungenAsText?: string;
    sachleistungenAsText?: string;
    showOnWebsite?: boolean;
    bemerkungen?: string;
    contactID?: number;
    itemId?: number;
}

const EckdatenProjektpartner: React.FC<EckdatenProjektpartnerProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    // State management
    const [projektpartner, setProjektpartner] = useState<PartnerWithRole[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Drawer state
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState<PartnerWithRole | null>(null);
    const [drawerMode, setDrawerMode] = useState<'view' | 'add' | 'addContact'>('view');
    const [selectedRole, setSelectedRole] = useState<PartnerRole | null>(null);

    // Project Partner Drawer state
    const [isProjectPartnerDrawerOpen, setIsProjectPartnerDrawerOpen] = useState(false);

    // Contact form state
    const [contactFormAnrede, setContactFormAnrede] = useState(false);
    const [contactFormTitelVor, setContactFormTitelVor] = useState('');
    const [contactFormVorname, setContactFormVorname] = useState('');
    const [contactFormNachname, setContactFormNachname] = useState('');
    const [contactFormTitelNach, setContactFormTitelNach] = useState('');
    const [contactFormFunktion, setContactFormFunktion] = useState('');
    const [contactFormEmail, setContactFormEmail] = useState('');
    const [contactFormTelefon, setContactFormTelefon] = useState('');

    // Add/Edit state
    const [partnerSearchResults, setPartnerSearchResults] = useState<ProjektPartnerItem[]>([]);
    const [selectedSearchPartner, setSelectedSearchPartner] = useState<ProjektPartnerItem | null>(null);
    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

    // Edit form state
    const [editedIsAktiv, setEditedIsAktiv] = useState(false);
    const [editedShowOnWebsite, setEditedShowOnWebsite] = useState(false);
    const [editedBemerkungen, setEditedBemerkungen] = useState('');
    const [editedContactId, setEditedContactId] = useState<number | null>(null);
    const [contacts, setContacts] = useState<AnsprechpartnerItem[]>([]);
    const [selectedImcRole, setSelectedImcRole] = useState<string>('');

    // Create dataService instance using MSAL context
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch project partners
    const fetchProjektpartner = useCallback(async () => {
        if (!dataService || !project?.projektID) {
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const partners = await dataService.getProjektpartnerListeAllForProject(project.projektID);
            console.log('Fetched project partners:', partners);
            setProjektpartner(partners as PartnerWithRole[]);
        } catch (err) {
            console.error('Error loading project partners:', err);
            setError('Fehler beim Laden der Projektpartner');
            setProjektpartner([]);
        } finally {
            setIsLoading(false);
        }
    }, [dataService, project?.projektID]);

    useEffect(() => {
        fetchProjektpartner();
    }, [fetchProjektpartner]);

    // Initialize IMC role from project
    useEffect(() => {
        if (project?.projektrolleImcID) {
            setSelectedImcRole(project.projektrolleImcID.toString());
        }
    }, [project?.projektrolleImcID]);

    // Group partners by role
    const partnersByRole = useMemo(() => {
        const grouped: Record<PartnerRole, PartnerWithRole[]> = {
            1: [],
            2: [],
            3: [],
            4: [],
        };

        projektpartner.forEach((partner) => {
            const role = partner.rolle as PartnerRole;
            if (role in grouped) {
                grouped[role].push(partner);
            }
        });

        return grouped;
    }, [projektpartner]);

    // Handle partner search
    const handlePartnerSearch = useCallback(
        (searchTerm: string) => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }

            if (searchTerm.length >= 3) {
                const timeout = setTimeout(async () => {
                    try {
                        const results = await dataService!.getProjektPartnerSearch(searchTerm);
                        setPartnerSearchResults(results);
                    } catch (error) {
                        console.error('Error searching partners:', error);
                        setPartnerSearchResults([]);
                    }
                }, 300);
                setSearchTimeout(timeout);
            } else {
                setPartnerSearchResults([]);
            }
        },
        [dataService, searchTimeout],
    );

    // Save edited partner
    const handleSavePartnerEdit = async () => {
        if (!dataService || !selectedPartner || !project?.projektID) return;

        try {
            setIsLoading(true);
            const contactInfo = contacts.find((c) => c.kontaktID === editedContactId) as AnsprechpartnerItem | undefined;
            const updatedPartner = {
                ...selectedPartner,
                isAktiv: editedIsAktiv,
                showOnWebsite: editedShowOnWebsite,
                bemerkungen: editedBemerkungen,
                contactID: editedContactId ?? undefined,
                contactTitle: contactInfo?.title ?? '',
                contactEmail: contactInfo?.email ?? '',
                contactTelefon: contactInfo?.telefon ?? '',
            } as unknown as ProjektPartnerZuweisungItem;

            await dataService.updateProjektpartner(updatedPartner);
            handleCloseDrawer();
            await fetchProjektpartner();
            // Update selected partner with new values
            setSelectedPartner({
                ...selectedPartner,
                isAktiv: editedIsAktiv,
                showOnWebsite: editedShowOnWebsite,
                bemerkungen: editedBemerkungen,
            });
        } catch (err) {
            console.error('Error saving partner:', err);
            setError('Error saving partner');
        } finally {
            setIsLoading(false);
        }
    };

    // Reset contact form
    const resetContactForm = () => {
        setContactFormAnrede(false);
        setContactFormTitelVor('');
        setContactFormVorname('');
        setContactFormNachname('');
        setContactFormTitelNach('');
        setContactFormFunktion('');
        setContactFormEmail('');
        setContactFormTelefon('');
    };

    // Handle adding new contact
    const handleSaveNewContact = async () => {
        if (!dataService || !selectedPartner?.partnerID) return;

        try {
            setIsLoading(true);
            const newContact = {
                kontaktID: 0,
                projektpartnerID: selectedPartner.partnerID,
                itemId: 0,
                anrede: contactFormAnrede ? 'Herr' : 'Frau',
                titel: contactFormTitelVor,
                vorname: contactFormVorname,
                nachname: contactFormNachname,
                titelAfter: contactFormTitelNach,
                name: `${contactFormVorname} ${contactFormNachname}`,
                fullNameWithGrades: `${contactFormTitelVor} ${contactFormVorname} ${contactFormNachname} ${contactFormTitelNach}`.trim(),
                fullNameWithGradesUppercase: `${contactFormTitelVor} ${contactFormVorname} ${contactFormNachname} ${contactFormTitelNach}`.trim().toUpperCase(),
                title: contactFormFunktion,
                funktion: contactFormFunktion,
                description: '',
                email: contactFormEmail,
                emailAsLink: contactFormEmail,
                telefon: contactFormTelefon,
                anzahlProjekte: 0,
                value: contactFormNachname,
                text: `${contactFormVorname} ${contactFormNachname}`,
                selected: false,
                code: 0,
                visibility: 'public',
                isAktiv: true,
                isAktivAsText: 'Active',
            };

            await dataService.addOrUpdateAnsprechpartner(newContact);
            resetContactForm();
            setDrawerMode('view');

            // Fetch updated partners and update contacts list
            if (project?.projektID) {
                const updated = await dataService.getProjektpartnerListeAllForProject(project.projektID);
                setProjektpartner(updated as PartnerWithRole[]);

                const updatedPartner = (updated as PartnerWithRole[]).find((p) => p.partnerID === selectedPartner?.partnerID);
                if (updatedPartner) {
                    setSelectedPartner(updatedPartner);
                    setContacts(updatedPartner.ansprechpersonen ?? []);
                }
            }
        } catch (err) {
            console.error('Error saving contact:', err);
            setError('Error adding contact');
        } finally {
            setIsLoading(false);
        }
    };

    // Open drawer for viewing/editing
    const handleOpenDrawer = (partner: PartnerWithRole | null, mode: 'view' | 'add' | 'addContact', role?: PartnerRole) => {
        setSelectedPartner(partner);
        setDrawerMode(mode);
        setSelectedRole(role || null);
        setIsDrawerOpen(true);
        setSelectedSearchPartner(null);
        setPartnerSearchResults([]);

        // Initialize edit fields if opening a partner in view mode
        if (mode === 'view' && partner) {
            setEditedIsAktiv(partner.isAktiv ?? false);
            setEditedShowOnWebsite(partner.showOnWebsite ?? false);
            setEditedBemerkungen(partner.bemerkungen ?? '');
            setEditedContactId(partner.contactID ?? null);
            setContacts(partner.ansprechpersonen ?? []);
            setSelectedImcRole('');
        }
    };

    // Close drawer
    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedPartner(null);
        setDrawerMode('view');
        setSelectedRole(null);
        setSelectedSearchPartner(null);
        setPartnerSearchResults([]);
        setSelectedImcRole('');
        resetContactForm();
    };

    // Handle IMC role change
    const handleImcRoleChange = async (value: string) => {
        if (!dataService || !project?.projektID) return;

        try {
            setSelectedImcRole(value);
            const imcRoleValue = parseInt(value);
            await dataService.updateProjektrolleIMC(project.projektID, imcRoleValue);
            // Optionally refresh the partners list
            await fetchProjektpartner();
        } catch (err) {
            console.error('Error updating IMC role:', err);
            setError('Fehler beim Aktualisieren der IMC-Rolle');
        }
    };

    // Delete partner
    const handleDeletePartner = async () => {
        if (!dataService || !selectedPartner?.partnerID || !project?.projektID) return;

        try {
            setIsLoading(true);
            await dataService.removeProjektpartner(project.projektID, selectedPartner.partnerID);
            handleCloseDrawer();
            await fetchProjektpartner();
        } catch (err) {
            console.error('Error deleting partner:', err);
            setError('Error removing partner');
        } finally {
            setIsLoading(false);
        }
    };

    // Save new partner assignment
    const handleSavePartnerAssignment = async () => {
        if (!dataService || !selectedSearchPartner || !selectedRole || !project?.projektID) return;

        try {
            setIsLoading(true);
            const partnerItem = {
                ...selectedSearchPartner,
                projektID: project.projektID,
                rolle: Number(selectedRole),
                isAktiv: true,
                aktivVon: new Date(),
                selected: false,
                code: 0,
                visibility: 'public',
                showOnWebsite: false,
                websiteStatus: '',
                usePersonalkosten: false,
                description: '',
                isDeleteEnabled: true,
                comments: '',
                finanzleistungen: 0,
                finanzleistungenAsText: '€0,00',
                personalleistungen: 0,
                personalleistungenAsText: 'PT0H',
                sachleistungen: 0,
                sachleistungenAsText: '€0,00',
                contactTitle: '',
                contactEmail: '',
                contactTelefon: '',
                bemerkungen: '',
                rolleAsText: PARTNER_ROLES[selectedRole],
                zuweisungsStatus: 'Active',
            };
            await dataService.addProjektpartner(partnerItem);
            handleCloseDrawer();
            await fetchProjektpartner();
        } catch (err) {
            console.error('Error saving partner assignment:', err);
            setError('Error adding partner');
        } finally {
            setIsLoading(false);
        }
    };

    if (!project) return null;

    if (isLoading && projektpartner.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading project partners..." />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {error && <div style={{ color: 'red', padding: tokens.spacingHorizontalM }}>{error}</div>}

            <div style={{ marginBottom: tokens.spacingVerticalL }}>
                <Button
                    icon={<Add24Regular />}
                    onClick={() => setIsProjectPartnerDrawerOpen(true)}
                    appearance="primary"
                >
                    Create Project Partner
                </Button>
            </div>

            <div style={{ maxWidth: '400px', marginBottom: tokens.spacingVerticalL }}>
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>IMC Role</label>
                    <Select
                        value={selectedImcRole}
                        onChange={(_, data: { value: string }) => handleImcRoleChange(data.value)}
                    >
                        <option value="">No Selection</option>
                        <option value="1">Sole Applicant/Provider</option>
                        <option value="2">Third-party Service Provider in Research Project</option>
                        <option value="3">Consortium Partner</option>
                        <option value="4">Lead</option>
                    </Select>
                </div>
            </div>

            <div className={styles.rolesGrid}>
                {(Object.entries(PARTNER_ROLES) as unknown as [PartnerRole, string][]).map(([roleId, roleName]) => {
                    const partners = partnersByRole[roleId];
                    const hasPartners = partners.length > 0;

                    return (
                        <div key={roleId} className={styles.roleSection}>
                            <div className={styles.roleTitle}>{roleName}</div>

                            {hasPartners ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                                    {partners.map((partner) => (
                                        console.log('Rendering partner card for:', partner, roleId),
                                        <Card
                                            key={partner.itemId}
                                            className={styles.partnerCard}
                                            onClick={() => handleOpenDrawer(partner, 'view')}
                                        >
                                            {/* Header with Name and Status Badge */}
                                            <div className={styles.cardHeader}>
                                                <div className={styles.partnerName}>{partner.bezeichnung1}</div>
                                                <div
                                                    className={`${styles.statusBadge} ${partner.isAktiv ? styles.statusActive : styles.statusInactive
                                                        }`}
                                                >
                                                    {partner.isAktiv ? 'Active' : 'Inactive'}
                                                </div>
                                            </div>

                                            {/* Contact Person */}
                                            {partner.ansprechpersonen && partner.ansprechpersonen.length > 0 && (
                                                <div className={styles.partnerDetail}>
                                                    <strong>Contact:</strong> {partner.ansprechpersonen[0].name || partner.ansprechpersonen[0].fullNameWithGrades}
                                                </div>
                                            )}

                                            {/* Address and Country combined */}
                                            {(partner.strasse || partner.plz || partner.ort || partner.nation) && (
                                                <div className={styles.partnerDetail}>
                                                    <strong>Address:</strong>{' '}
                                                    {[
                                                        partner.strasse,
                                                        partner.plz && partner.ort ? `${partner.plz} ${partner.ort}` : null,
                                                        partner.nation,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(', ')}
                                                </div>
                                            )}

                                            {/* Finance Partner Code */}
                                            {partner.finanzPartnerCodeBezeichnung && (
                                                <div className={styles.partnerDetail}>
                                                    <strong>Finance Partner Code:</strong> {partner.finanzPartnerCodeBezeichnung}
                                                </div>
                                            )}

                                            {/* Cooperation Partner Code */}
                                            {partner.koopPartnerCodeBezeichnung && (
                                                <div className={styles.partnerDetail}>
                                                    <strong>Cooperation Partner Code:</strong> {partner.koopPartnerCodeBezeichnung}
                                                </div>
                                            )}

                                            {/* Financing Contribution Net */}
                                            {partner.finanzleistungenAsText && (
                                                <div className={styles.partnerDetail}>
                                                    <strong>Financing Contribution (Net):</strong> {partner.finanzleistungenAsText}
                                                </div>
                                            )}

                                            {/* External Costs (Personnel) */}
                                            {partner.personalleistungenAsText && (
                                                <div className={styles.partnerDetail}>
                                                    <strong>External Costs:</strong> {partner.personalleistungenAsText}
                                                </div>
                                            )}

                                            {/* Other External Costs (Material) */}
                                            {partner.sachleistungenAsText && (
                                                <div className={styles.partnerDetail}>
                                                    <strong>Other External Costs:</strong> {partner.sachleistungenAsText}
                                                </div>
                                            )}
                                        </Card>
                                    ))}
                                    {(roleId.toString() !== '1' && roleId.toString() !== '2') && (
                                        console.log('Rendering add button for role:', roleId),
                                        <Button
                                            icon={<Add24Regular />}
                                            onClick={() => handleOpenDrawer(null, 'add', roleId)}
                                        >
                                            Add Partner
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <Card
                                    className={styles.addPartnerCard}
                                    onClick={() => handleOpenDrawer(null, 'add', roleId)}
                                >
                                    <div className={styles.addButtonContent}>
                                        <Add24Regular fontSize={32} />
                                        <span>Add Partner</span>
                                    </div>
                                </Card>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Detail/Add Drawer */}
            <Drawer open={isDrawerOpen} size="medium" onOpenChange={(_, { open }) => !open && handleCloseDrawer()} position="end">
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={handleCloseDrawer} />
                        }
                    >
                        {drawerMode === 'view' ? 'Project Partner Details' : drawerMode === 'add' ? 'Add Project Partner' : 'Add Contact'}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerContent}>
                        {drawerMode === 'view' && selectedPartner && (
                            <>
                                <div className={styles.detailsGroup}>
                                    <div>
                                        <div className={styles.detailLabel}>Name</div>
                                        <div className={styles.detailValue}>{selectedPartner.bezeichnung1}</div>
                                    </div>
                                </div>

                                {selectedPartner.nation && (
                                    <div className={styles.detailsGroup}>
                                        <div>
                                            <div className={styles.detailLabel}>Country</div>
                                            <div className={styles.detailValue}>{selectedPartner.nation}</div>
                                        </div>
                                    </div>
                                )}

                                {(selectedPartner.strasse || selectedPartner.plz || selectedPartner.ort) && (
                                    <div className={styles.detailsGroup}>
                                        <div>
                                            <div className={styles.detailLabel}>Address</div>
                                            <div className={styles.detailValue}>
                                                {selectedPartner.strasse && <div>{selectedPartner.strasse}</div>}
                                                <div>
                                                    {selectedPartner.plz} {selectedPartner.ort}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedPartner.finanzPartnerCodeBezeichnung && (
                                    <div className={styles.detailsGroup}>
                                        <div>
                                            <div className={styles.detailLabel}>Finance Partner Code</div>
                                            <div className={styles.detailValue}>
                                                {selectedPartner.finanzPartnerCodeBezeichnung}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedPartner.koopPartnerCodeBezeichnung && (
                                    <div className={styles.detailsGroup}>
                                        <div>
                                            <div className={styles.detailLabel}>Cooperation Partner Code</div>
                                            <div className={styles.detailValue}>
                                                {selectedPartner.koopPartnerCodeBezeichnung}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedPartner.finanzleistungenAsText && (
                                    <div className={styles.detailsGroup}>
                                        <div>
                                            <div className={styles.detailLabel}>Financing Contribution (Net)</div>
                                            <div className={styles.detailValue}>{selectedPartner.finanzleistungenAsText}</div>
                                        </div>
                                    </div>
                                )}

                                {selectedPartner.personalleistungenAsText && (
                                    <div className={styles.detailsGroup}>
                                        <div>
                                            <div className={styles.detailLabel}>Personnel Contribution</div>
                                            <div className={styles.detailValue}>{selectedPartner.personalleistungenAsText}</div>
                                        </div>
                                    </div>
                                )}

                                {selectedPartner.sachleistungenAsText && (
                                    <div className={styles.detailsGroup}>
                                        <div>
                                            <div className={styles.detailLabel}>Material Contribution</div>
                                            <div className={styles.detailValue}>{selectedPartner.sachleistungenAsText}</div>
                                        </div>
                                    </div>
                                )}

                                {/* Editable Fields Section */}
                                <div className={styles.detailsGroup} style={{ marginTop: tokens.spacingVerticalXL }}>
                                    <div style={{ borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: tokens.spacingVerticalL }}>
                                        <div className={styles.formGroup}>
                                            <label className={styles.formLabel}>Active</label>
                                            <Switch
                                                checked={editedIsAktiv}
                                                onChange={(_: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) =>
                                                    setEditedIsAktiv(data.checked)
                                                }
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label className={styles.formLabel}>Display on PURE</label>
                                            <Switch
                                                checked={editedShowOnWebsite}
                                                onChange={(_: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) =>
                                                    setEditedShowOnWebsite(data.checked)
                                                }
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label className={styles.formLabel}>Contact Person</label>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
                                                <Combobox
                                                    value={
                                                        editedContactId
                                                            ? contacts.find((c) => c.kontaktID === editedContactId)?.name || ''
                                                            : ''
                                                    }
                                                    onOptionSelect={(_, option) => {
                                                        const contactId = parseInt(option.optionValue || '0');
                                                        setEditedContactId(contactId === 0 ? null : contactId || null);
                                                    }}
                                                >
                                                    <Option key="clear" value="0">
                                                        No Contact
                                                    </Option>
                                                    {contacts.map((contact) => (
                                                        <Option key={contact.kontaktID} value={contact.kontaktID.toString()}>
                                                            {contact.name || contact.fullNameWithGrades}
                                                        </Option>
                                                    ))}
                                                </Combobox>
                                                <Link onClick={() => setDrawerMode('addContact')} style={{ cursor: 'pointer' }}>
                                                    + Add Contact
                                                </Link>
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label className={styles.formLabel}>Notes</label>
                                            <Textarea
                                                value={editedBemerkungen}
                                                onChange={(_, data: { value: string }) => setEditedBemerkungen(data.value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.buttonGroup}>
                                    <Button onClick={handleSavePartnerEdit} appearance="primary">
                                        Save
                                    </Button>
                                    <Button
                                        icon={<Delete24Regular />}
                                        onClick={handleDeletePartner}
                                        appearance="outline"
                                        style={{ color: 'red' }}
                                    >
                                        Remove
                                    </Button>
                                    <Button onClick={handleCloseDrawer}>Close</Button>
                                </div>
                            </>
                        )}

                        {drawerMode === 'add' && (
                            <>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Role</label>
                                    <div className={styles.detailValue}>{selectedRole ? PARTNER_ROLES[selectedRole] : 'Not selected'}</div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Search Project Partner</label>
                                    <Combobox
                                        placeholder="Enter at least 3 characters..."
                                        onOptionSelect={(_, option) => {
                                            const partnerId = parseInt(option.optionValue || '0');
                                            const partner = partnerSearchResults.find((p) => p.partnerID === partnerId);
                                            if (partner) {
                                                setSelectedSearchPartner(partner);
                                            }
                                        }}
                                        onChange={(ev) => {
                                            const value = (ev.target as HTMLInputElement).value;
                                            handlePartnerSearch(value);
                                        }}
                                    >
                                        {partnerSearchResults.map((partner) => (
                                            <Option key={partner.partnerID} value={partner.partnerID.toString()}>
                                                {partner.bezeichnung1}
                                            </Option>
                                        ))}
                                    </Combobox>
                                </div>

                                {selectedSearchPartner && (
                                    <div className={styles.detailsGroup}>
                                        <div>
                                            <div className={styles.detailLabel}>Selected Partner</div>
                                            <div className={styles.detailValue}>{selectedSearchPartner.bezeichnung1}</div>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.buttonGroup}>
                                    <Button onClick={handleSavePartnerAssignment} disabled={!selectedSearchPartner} appearance="primary">
                                        Add Project Partner
                                    </Button>
                                    <Button onClick={handleCloseDrawer}>Cancel</Button>
                                </div>
                            </>
                        )}

                        {drawerMode === 'addContact' && selectedPartner && (
                            <>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Salutation</label>
                                    <Switch
                                        checked={contactFormAnrede}
                                        onChange={(_: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) =>
                                            setContactFormAnrede(data.checked)
                                        }
                                        label={contactFormAnrede ? 'Mr.' : 'Ms.'}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Title before Name</label>
                                    <Input
                                        type="text"
                                        value={contactFormTitelVor}
                                        onChange={(_, data: { value: string }) => setContactFormTitelVor(data.value)}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>First Name</label>
                                    <Input
                                        type="text"
                                        value={contactFormVorname}
                                        onChange={(_, data: { value: string }) => setContactFormVorname(data.value)}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Last Name</label>
                                    <Input
                                        type="text"
                                        value={contactFormNachname}
                                        onChange={(_, data: { value: string }) => setContactFormNachname(data.value)}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Title after Name</label>
                                    <Input
                                        type="text"
                                        value={contactFormTitelNach}
                                        onChange={(_, data: { value: string }) => setContactFormTitelNach(data.value)}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Position</label>
                                    <Input
                                        type="text"
                                        value={contactFormFunktion}
                                        onChange={(_, data: { value: string }) => setContactFormFunktion(data.value)}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Email</label>
                                    <Input
                                        type="email"
                                        value={contactFormEmail}
                                        onChange={(_, data: { value: string }) => setContactFormEmail(data.value)}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Phone</label>
                                    <Input
                                        type="tel"
                                        value={contactFormTelefon}
                                        onChange={(_, data: { value: string }) => setContactFormTelefon(data.value)}
                                    />
                                </div>

                                <div className={styles.buttonGroup}>
                                    <Button onClick={handleSaveNewContact} appearance="primary">
                                        Save
                                    </Button>
                                    <Button onClick={() => { resetContactForm(); setDrawerMode('view'); }}>Cancel</Button>
                                </div>
                            </>
                        )}
                    </div>
                </DrawerBody>
            </Drawer>

            {/* Project Partner Creation Drawer */}
            <ProjektpartnerDrawer
                isOpen={isProjectPartnerDrawerOpen}
                partnerId={null}
                isEditMode={false}
                onClose={() => setIsProjectPartnerDrawerOpen(false)}
                onSave={() => {
                    setIsProjectPartnerDrawerOpen(false);
                    fetchProjektpartner();
                }}
            />
        </div>
    );
};

export default EckdatenProjektpartner;
